import type { CSSProperties } from 'react'
import { toPublicLocale, type PublicLocale } from '@/lib/locale-prefix'
import {
  getHomeHref,
  getReyesHref,
  getHadaLandingHref,
  getRatonPerezLandingPath,
  getMobileHref,
} from '@/lib/locale-paths'
import { getMundoMagicoHref } from '@/lib/generic-landings/paths'
import { CHARACTER_THEMES } from '@/lib/character-themes'

/**
 * Tabla de presentación de las pantallas de acceso (`AuthScreen`).
 *
 * Antes cada superficie se decidía con un encadenado `isReyes ? … : isHada ? …`
 * repetido ~49 veces en el componente: añadir una variante obligaba a tocar las
 * 49. Aquí cada variante declara sus clases una vez y el componente hace
 * lookup. Las clases son strings Tailwind COMPLETAS (nunca construidas de forma
 * dinámica) para que el escáner de contenido de Tailwind v4 las genere — misma
 * regla que `lib/character-themes.ts`.
 */
export type AuthVariant = 'santa' | 'reyes' | 'hada' | 'raton-perez' | 'generic'

export type AuthNamespace =
  | 'auth'
  | 'authReyes'
  | 'authHada'
  | 'authRatonPerez'
  | 'authGeneric'

export interface AuthDecoration {
  className: string
  content: string
  style?: CSSProperties
}

export interface AuthVariantStyle {
  /** Namespace de next-intl con los textos de la pantalla. */
  namespace: AuthNamespace
  /**
   * La variante pinta sobre un fondo oscuro de personaje (texto en blanco).
   * `santa` es la única que usa los tokens de tema claro/oscuro del sistema.
   */
  onDark: boolean
  /** Gradiente de página; vacío en `santa` (hereda el fondo de la app). */
  pageGradient: string
  card: string
  primaryButton: string
  resendButton: string
  backLink: string
  emailInput: string
  codeInput: string
  otpDigit: string
  /** Botones de acceso social (`SocialSignInControls`). */
  socialButton: { google: string; apple: string }
  decorations: AuthDecoration[]
  footerIcons: string[]
  /** Landing a la que vuelve el enlace "atrás" cuando no se pasa `backHref`. */
  landingHref: (locale: PublicLocale) => string
}

const DARK_CARD = 'border border-white/10'
const AMBER_BACK_LINK = 'text-amber-200 hover:text-amber-100'
const AMBER_PRIMARY_BUTTON = 'bg-amber-400 text-amber-950 hover:bg-amber-300'
/** Botón social sobre fondo oscuro: pastilla blanca con el acento del personaje. */
const WHITE_SOCIAL_BUTTON = 'border-white/20 bg-white hover:bg-amber-50'

function decorations(topLeft: string, topRight: string, midLeft: string, midRight: string): AuthDecoration[] {
  return [
    { className: 'absolute top-4 left-4 text-4xl twinkle', content: topLeft },
    { className: 'absolute top-4 right-4 text-4xl twinkle', content: topRight, style: { animationDelay: '0.5s' } },
    { className: 'absolute top-12 left-1/4 text-2xl twinkle', content: midLeft, style: { animationDelay: '1s' } },
    { className: 'absolute top-12 right-1/4 text-2xl twinkle', content: midRight, style: { animationDelay: '1.5s' } },
  ]
}

export const AUTH_VARIANT_STYLES: Record<AuthVariant, AuthVariantStyle> = {
  santa: {
    namespace: 'auth',
    onDark: false,
    pageGradient: '',
    card: 'bg-card/80',
    primaryButton: 'bg-secondary text-secondary-foreground',
    resendButton: 'text-accent hover:text-accent/80',
    backLink: 'text-muted-foreground hover:text-foreground',
    emailInput: '',
    codeInput: '',
    otpDigit: '',
    socialButton: {
      google: 'border-input bg-background text-foreground hover:bg-muted',
      apple: 'border-black bg-black text-white hover:bg-black/90',
    },
    decorations: decorations('⭐', '🔔', '✨', '✨'),
    footerIcons: ['🎄', '🎁', '🦌', '🍪', '🎄'],
    landingHref: getHomeHref,
  },
  reyes: {
    namespace: 'authReyes',
    onDark: true,
    pageGradient: CHARACTER_THEMES.reyes.landingGradient,
    card: `bg-gradient-to-br from-slate-900/80 via-blue-900/70 to-amber-900/40 ${DARK_CARD}`,
    primaryButton: AMBER_PRIMARY_BUTTON,
    resendButton: AMBER_BACK_LINK,
    backLink: AMBER_BACK_LINK,
    emailInput: 'bg-white/10 text-white placeholder:text-white/60 border-white/20 focus:ring-amber-300',
    codeInput: 'bg-white/10 text-white border-white/20 focus:ring-amber-300',
    otpDigit: 'bg-white/10 text-white border-white/30 focus:border-amber-300 focus:ring-amber-300/30',
    socialButton: {
      google: `${WHITE_SOCIAL_BUTTON} text-slate-950`,
      apple: `${WHITE_SOCIAL_BUTTON} text-slate-950`,
    },
    decorations: decorations('👑', '✨', '🌟', '🕯️'),
    footerIcons: ['🌟', '👑', '🎁', '🕯️', '🌟'],
    landingHref: getReyesHref,
  },
  hada: {
    namespace: 'authHada',
    onDark: true,
    pageGradient: 'bg-gradient-to-b from-indigo-950 via-purple-950 to-pink-950',
    card: `bg-gradient-to-br from-violet-900/80 via-purple-900/70 to-pink-900/40 ${DARK_CARD}`,
    primaryButton: 'bg-violet-400 text-violet-950 hover:bg-violet-300',
    resendButton: 'text-violet-200 hover:text-violet-100',
    backLink: 'text-violet-200 hover:text-violet-100',
    emailInput: 'bg-white/10 text-white placeholder:text-white/60 border-white/20 focus:ring-violet-300',
    codeInput: 'bg-white/10 text-white border-white/20 focus:ring-violet-300',
    otpDigit: 'bg-white/10 text-white border-white/30 focus:border-violet-300 focus:ring-violet-300/30',
    socialButton: {
      google: 'border-white/20 bg-white text-violet-950 hover:bg-violet-50',
      apple: 'border-white/20 bg-white text-violet-950 hover:bg-violet-50',
    },
    decorations: decorations('🧚‍♀️', '✨', '🦷', '💫'),
    footerIcons: ['🧚‍♀️', '🦷', '✨', '💫', '🧚‍♀️'],
    landingHref: getHadaLandingHref,
  },
  'raton-perez': {
    namespace: 'authRatonPerez',
    onDark: true,
    pageGradient: CHARACTER_THEMES['raton-perez'].landingGradient,
    card: `bg-gradient-to-br from-amber-900/80 via-stone-900/70 to-amber-900/40 ${DARK_CARD}`,
    primaryButton: AMBER_PRIMARY_BUTTON,
    resendButton: AMBER_BACK_LINK,
    backLink: AMBER_BACK_LINK,
    emailInput: 'bg-white/10 text-white placeholder:text-white/60 border-white/20 focus:ring-amber-300',
    codeInput: 'bg-white/10 text-white border-white/20 focus:ring-amber-300',
    otpDigit: 'bg-white/10 text-white border-white/30 focus:border-amber-300 focus:ring-amber-300/30',
    socialButton: {
      google: `${WHITE_SOCIAL_BUTTON} text-amber-950`,
      apple: `${WHITE_SOCIAL_BUTTON} text-amber-950`,
    },
    decorations: decorations('🐭', '✨', '🦷', '💫'),
    footerIcons: ['🐭', '🦷', '✨', '💫', '🐭'],
    landingHref: getRatonPerezLandingPath,
  },
  // Acceso compartido por las landings del catálogo genérico (Conejo de Pascua,
  // Halloween y Mundo Mágico): una sola página en vez de una por landing, porque
  // el copy sería casi idéntico y competirían entre sí en búsqueda. El back-link
  // concreto lo inyecta la página desde su `?from=`.
  generic: {
    namespace: 'authGeneric',
    onDark: true,
    pageGradient: CHARACTER_THEMES.generic.landingGradient,
    card: `bg-gradient-to-br from-slate-900/80 via-indigo-900/70 to-sky-900/40 ${DARK_CARD}`,
    primaryButton: 'bg-sky-400 text-sky-950 hover:bg-sky-300',
    resendButton: 'text-sky-200 hover:text-sky-100',
    backLink: 'text-sky-200 hover:text-sky-100',
    emailInput: 'bg-white/10 text-white placeholder:text-white/60 border-white/20 focus:ring-sky-300',
    codeInput: 'bg-white/10 text-white border-white/20 focus:ring-sky-300',
    otpDigit: 'bg-white/10 text-white border-white/30 focus:border-sky-300 focus:ring-sky-300/30',
    socialButton: {
      google: 'border-white/20 bg-white text-sky-950 hover:bg-sky-50',
      apple: 'border-white/20 bg-white text-sky-950 hover:bg-sky-50',
    },
    decorations: decorations('✨', '🌟', '🦄', '🔮'),
    footerIcons: ['✨', '🦄', '🐉', '🧜‍♀️', '✨'],
    landingHref: getMundoMagicoHref,
  },
}

export function getAuthVariantStyle(variant: AuthVariant): AuthVariantStyle {
  return AUTH_VARIANT_STYLES[variant]
}

/**
 * Destino del enlace "atrás" cuando la página no impone uno. En la app nativa
 * siempre vuelve a `/mobile`: las landings web no forman parte de ese flujo.
 */
export function getDefaultBackHref(
  variant: AuthVariant,
  locale: string,
  isNativeApp: boolean
): string {
  const pl = toPublicLocale(locale)
  if (isNativeApp) return getMobileHref(pl)
  return AUTH_VARIANT_STYLES[variant].landingHref(pl)
}
